<template>
  <v-card>
    <v-toolbar v-if="!hideHeader" :dark="$vuetify.theme.dark" color="primary">
      <v-row align="center">
        <v-col cols="10" class="text-left">
          <v-toolbar-title class="textwithbackground--text">
            {{ $t('Состав торговой процедуры') }}
          </v-toolbar-title>
        </v-col>
        <v-col cols="2" class="text-right">
          <btn-icon-tip
              v-if="canAdd"
              icon="add"
              icon-color="secondaryiconbtn"
              :tip="$t('Добавить позицию')"
              @click="openAddPositionDialog" />
        </v-col>
      </v-row>
    </v-toolbar>
    <v-row>
      <v-col>
        <v-data-table
            :headers="headers"
            :items="positions"
            :items-per-page="itemsPerPage"
            item-key="positionId"
            disable-sort-by
            :dense="dense"
            :loading="isLoading"
            :locale="systemLocale">
          <template v-slot:progress>
            <v-progress-linear :active="isLoading" color="loader" indeterminate />
          </template>
          <!-- eslint-disable-next-line vue/valid-v-slot -->
          <template v-slot:item.positionNumber="{ item }">
            {{ item.positionNumber }}
          </template>
          <!-- eslint-disable-next-line vue/valid-v-slot -->
          <template v-slot:item.positionDescription="{ item }">
            {{ item.positionDescription }}
          </template>
          <!-- eslint-disable-next-line vue/valid-v-slot -->
          <template v-slot:item.amount="{ item }">
            {{ item.amount }}
          </template>
          <!-- eslint-disable-next-line vue/valid-v-slot -->
          <template v-slot:item.startPremium="{ item }">
            {{ item.startPremium }}
          </template>
          <!-- eslint-disable-next-line vue/valid-v-slot -->
          <template v-slot:item.positionId="{ item }">
            <btn-icon-tip
                :id="`viewBtn-${item.positionId}`"
                :tip="$t('Просмотреть позицию')"
                icon="visibility"
                @click="openEditPositionDialog(item.positionId, true)" />
            <btn-icon-tip
                v-if="canEdit"
                :id="`editBtn-${item.positionId}`"
                :tip="$t('Редактировать позицию')"
                icon="edit"
                @click="openEditPositionDialog(item.positionId, false )" />
            <btn-icon-tip
                v-if="canEdit"
                :id="`deleteBtn-${item.positionId}`"
                :tip="$t('Удалить позицию')"
                icon="delete_forever"
                @click="openConfirmationBeforDelete(item.positionId)" />
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <confirm-dialog
        v-if="confirmDialog.show"
        :dialog-prop="confirmDialog"
        @close="closeDialog('confirmDialog')" />
    <add-position-dialog
        v-if="addPositionDialog.show"
        :dialog-prop="addPositionDialog"
        @close="closeDialog('addPositionDialog')" />
    <edit-position-dialog
        v-if="editPositionDialog.show"
        :dialog-prop="editPositionDialog"
        @close="closeDialog('editPositionDialog')" />
  </v-card>
</template>

<script>

  import BtnIconTip from '../ui/BtnIconTip.vue'
  import AddPositionDialog from './AddPositionDialog.vue'
  import EditPositionDialog from './EditPositionDialog.vue'
  import ConfirmDialog from '../common/ConfirmDialog.vue'
  import { mapActions } from 'vuex'
  import { LOT_POSITIONS_MANAGEMENT } from '@/constants/store.js'
  import { PREMIUM_TYPES } from '@/constants/business.js'

  export default {
    components: {
      BtnIconTip,
      AddPositionDialog,
      EditPositionDialog,
      ConfirmDialog
    },
    props: {
      canAdd: {
        type: Boolean,
        default: false
      },
      canEdit: {
        type: Boolean,
        default: false
      },
      hideDescription: {
        type: Boolean,
        default: false
      },
      dense: {
        type: Boolean,
        default: false
      },
      hideHeader: {
        type: Boolean,
        default: false
      },
      positions: {
        type: Array,
        required: true
      },
      lot: {
        type: Object,
        required: true,
        default: () => ({
          lotType: null,
          lotId: null,
          premiumType: null
        })
      },
      loading: {
        type: Boolean,
        default: false
      }
    },
    data () {
      return {
        isLoading: false,
        itemsPerPage: 5,
        addPositionDialog: {
          lot: null,
          show: false,
          callback: null
        },
        confirmDialog: {
          show: false,
          text: null,
          additional: null,
          callback: null
        },
        editPositionDialog: {
          positionId: null,
          lot: null,
          callback: null,
          show: false,
          readonly: false
        }
      }
    },
    computed: {
      headers () {
        let headers = [
          { text: this.$t('№ позиции'), align: 'start', sortable: false, value: 'positionNumber' },
          { text: this.$t('Наименование позиции'), align: 'start', sortable: false, value: 'positionDescription' },
          { text: this.lot.premiumType === PREMIUM_TYPES.FIX ? this.$t('Количество') : this.$t('Общее количество, тонн'), align: 'start', sortable: false, value: 'amount' },
          { text: this.lot.premiumType === PREMIUM_TYPES.FIX ? this.$t('Начальная цена за единицу в валюте Контракта') : this.$t('Стартовое значение дифференциала'), align: 'start', sortable: false, value: 'startPremium' },
          { text: this.$t('Действия'), align: 'center', sortable: false, value: 'positionId' }
        ]
        if (!this.showStartPremium) {
          headers = headers.filter(h => h.value !== 'startPremium')
        }
        if (this.hideDescription) {
          headers = headers.filter(h => h.value !== 'positionDescription')
        }
        return headers
      },
      showStartPremium () {
        return (!this.lot.hasBasises || !this.isParticipant) &&
          this.positions.some(p => !!p.startPremium)
      }
    },
    watch: {
      loading (newValue) {
        this.isLoading = newValue
      }
    },
    methods: {
      ...mapActions(LOT_POSITIONS_MANAGEMENT.NAMESPACE, {
        deletePositionAction: LOT_POSITIONS_MANAGEMENT.ACTIONS.DELETE_POSITION
      }),
      deletePosition: async function (positionId) {
        try {
          this.isLoading = true
          const model = {
            positionId: positionId,
            lotType: this.lot.lotType,
            lotId: this.lot.lotId
          }
          await Promise.all([
            this.deletePositionAction(model)
          ])

          this.isLoading = false
          this.edited()
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      openAddPositionDialog: function () {
        this.addPositionDialog.lot = this.lot
        this.addPositionDialog.callback = this.edited
        this.addPositionDialog.show = true
      },
      openEditPositionDialog: function (positionId, readonly) {
        this.editPositionDialog.positionId = positionId
        this.editPositionDialog.lot = this.lot
        this.editPositionDialog.callback = this.edited
        this.editPositionDialog.readonly = readonly
        this.editPositionDialog.show = true
      },
      openConfirmationBeforDelete: function (id) {
        this.confirmDialog.warning = true
        this.confirmDialog.additional = id
        this.confirmDialog.text = this.$t('Позиция будет удалена. Продолжить?')
        this.confirmDialog.callback = this.deletePosition
        this.confirmDialog.show = true
      },
      edited: function () {
        this.$emit('change')
      }
    }
  }
</script>
